import { apiClient } from "@/lib/api-client";
import type { ApiResponse } from "@/types/api";
import type {
  SubjectArea,
  SubjectAreaCreateRequest,
  SubjectAreaUpdateRequest,
} from "../types";

export async function listSubjectAreas(
  scheduleId: string,
): Promise<SubjectArea[]> {
  const response = await apiClient.get<ApiResponse<SubjectArea[]>>(
    `/schedules/${scheduleId}/subject-areas`,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to load subject areas");
}

export async function createSubjectArea(
  scheduleId: string,
  payload: SubjectAreaCreateRequest,
): Promise<SubjectArea> {
  const response = await apiClient.post<ApiResponse<SubjectArea>>(
    `/schedules/${scheduleId}/subject-areas`,
    payload,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to create subject area");
}

export async function updateSubjectArea(
  scheduleId: string,
  areaId: string,
  payload: SubjectAreaUpdateRequest,
): Promise<SubjectArea> {
  const response = await apiClient.put<ApiResponse<SubjectArea>>(
    `/schedules/${scheduleId}/subject-areas/${areaId}`,
    payload,
  );
  if (response.data.success) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to update subject area");
}

export async function deleteSubjectArea(
  scheduleId: string,
  areaId: string,
): Promise<void> {
  const response = await apiClient.delete<ApiResponse<null> | "">(
    `/schedules/${scheduleId}/subject-areas/${areaId}`,
  );
  // 204 No Content yields an empty body — treat as success.
  if (response.status === 204) return;
  if (typeof response.data === "object" && response.data !== null) {
    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to delete subject area");
    }
  }
}

export async function reorderSubjectAreas(
  scheduleId: string,
  orderedIds: string[],
): Promise<void> {
  const response = await apiClient.patch<ApiResponse<null> | "">(
    `/schedules/${scheduleId}/subject-areas/order`,
    { orderedIds },
  );
  if (response.status === 204) return;
  if (typeof response.data === "object" && response.data !== null) {
    if (!response.data.success) {
      throw new Error(
        response.data.message || "Failed to reorder subject areas",
      );
    }
  }
}
